import { addDays, payPeriodContaining } from './shifts'
import type { DayLine, LocalDate, PayPeriod } from './shifts'
import { daysBetween, weekdayOf } from './schedule'

/**
 * A month on a wall calendar: the arithmetic behind the availability and hours
 * calendars, so the grid a phone draws and the totals printed above it come
 * from functions with tests rather than from a component's loop.
 *
 * **Weeks start on Monday**, as a workshop rota does — and as the pay periods
 * do not, which is why the boundary between them is computed here
 * (`payPeriodEdges`) rather than guessed at in CSS.
 *
 * Everything is calendar dates (`LocalDate`) — no instants, no zone, no clock.
 * "Today" is always a parameter.
 */

/** `YYYY-MM`: a calendar month with no zone attached. */
export type LocalMonth = string & { readonly __brand: 'LocalMonth' }

const pad = (n: number): string => String(n).padStart(2, '0')

function ym(month: LocalMonth): [number, number] {
  const [y, m] = month.split('-').map(Number)
  return [y ?? 0, m ?? 1]
}

export function monthOf(date: LocalDate): LocalMonth {
  return date.slice(0, 7) as LocalMonth
}

export function firstOfMonth(month: LocalMonth): LocalDate {
  return `${month}-01` as LocalDate
}

export function daysInMonth(month: LocalMonth): number {
  const [y, m] = ym(month)
  return new Date(Date.UTC(y, m, 0)).getUTCDate()
}

export function lastOfMonth(month: LocalMonth): LocalDate {
  return `${month}-${pad(daysInMonth(month))}` as LocalDate
}

export function addMonths(month: LocalMonth, n: number): LocalMonth {
  const [y, m] = ym(month)
  const t = new Date(Date.UTC(y, m - 1 + n, 1))
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}` as LocalMonth
}

/** Held inside `[min, max]`; either bound may be absent. */
export function clampMonth(month: LocalMonth, min?: LocalMonth | null, max?: LocalMonth | null): LocalMonth {
  if (min && month < min) return min
  if (max && month > max) return max
  return month
}

const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** `October 2026`. */
export function monthLabel(month: LocalMonth): string {
  const [y, m] = ym(month)
  return `${MONTH_LONG[m - 1]} ${y}`
}

/** `Thursday 22 October` — what a screen reader says for a day cell. */
export function longDayLabel(date: LocalDate): string {
  const [, m, d] = date.split('-').map(Number)
  return `${WEEKDAY_LONG[weekdayOf(date)]} ${d} ${MONTH_LONG[(m ?? 1) - 1]}`
}

/** The column headings, Monday first. */
export const WEEK_HEADINGS: readonly string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export interface GridDay {
  readonly date: LocalDate
  /** Day of the month, 1–31. */
  readonly day: number
  /** False for the leading and trailing days that pad the first and last week. */
  readonly inMonth: boolean
  /** 0 = Sunday … 6 = Saturday, as `weekdayOf`. */
  readonly weekday: number
  readonly weekend: boolean
}

/** 0 = Monday … 6 = Sunday: the column a date sits in. */
export function columnOf(date: LocalDate): number {
  return (weekdayOf(date) + 6) % 7
}

/**
 * The month as whole Monday-to-Sunday weeks — four to six of them — padded
 * with the neighbouring months' days so every row has seven cells.
 */
export function monthGrid(month: LocalMonth): readonly (readonly GridDay[])[] {
  const first = firstOfMonth(month)
  const last = lastOfMonth(month)
  const start = addDays(first, -columnOf(first))
  const end = addDays(last, 6 - columnOf(last))
  const weeks: GridDay[][] = []
  for (let date = start; date <= end; date = addDays(date, 1)) {
    if (columnOf(date) === 0) weeks.push([])
    const weekday = weekdayOf(date)
    weeks[weeks.length - 1]?.push({
      date,
      day: Number(date.slice(8)),
      inMonth: date >= first && date <= last,
      weekday,
      weekend: weekday === 0 || weekday === 6,
    })
  }
  return weeks
}

/** Every date from `a` to `b` inclusive, in order, whichever way round they are given. */
export function datesBetween(a: LocalDate, b: LocalDate): readonly LocalDate[] {
  const [from, to] = a <= b ? [a, b] : [b, a]
  return Array.from({ length: daysBetween(from, to) + 1 }, (_, i) => addDays(from, i))
}

export type CalendarKey = 'left' | 'right' | 'up' | 'down' | 'weekStart' | 'weekEnd'

/**
 * Where an arrow key takes the focus. Held inside the month — the month
 * buttons are how you leave it — so the key never lands on a padding cell.
 */
export function moveInMonth(date: LocalDate, key: CalendarKey): LocalDate {
  const month = monthOf(date)
  const step: Record<CalendarKey, number> = {
    left: -1,
    right: 1,
    up: -7,
    down: 7,
    weekStart: -columnOf(date),
    weekEnd: 6 - columnOf(date),
  }
  const target = addDays(date, step[key])
  if (target < firstOfMonth(month)) return firstOfMonth(month)
  if (target > lastOfMonth(month)) return lastOfMonth(month)
  return target
}

/**
 * Where the line between a month's two pay periods runs on a Monday-first
 * grid: a staircase, because the 16th is rarely a Monday. Cells of the second
 * period draw the line on their **top** edge where the cell above them is in
 * the first period, and the 16th draws it on its **left** edge unless it opens
 * the row.
 */
export function payPeriodEdges(date: LocalDate): { readonly top: boolean; readonly left: boolean } {
  const day = Number(date.slice(8))
  if (day < 16) return { top: false, left: false }
  return { top: day <= 22, left: day === 16 && columnOf(date) !== 0 }
}

/** The two pay periods a month is split into, first half first. */
export function payPeriodsOf(month: LocalMonth): readonly [PayPeriod, PayPeriod] {
  return [payPeriodContaining(firstOfMonth(month)), payPeriodContaining(`${month}-16` as LocalDate)]
}

// --- hours on a calendar ----------------------------------------------------

/** A day that needs somebody to look at it: a forgotten clock-out, or work with no shift. */
export function dayNeedsALook(day: Pick<DayLine, 'shifts' | 'workWithoutShift'>): boolean {
  return day.workWithoutShift || day.shifts.some((s) => s.forgotten)
}

/** A full day, for the fill bar under a day's hours: eight hours fills it. */
export const FULL_DAY_MINUTES = 480

/** How full a day's bar is drawn, 0–1. Past a full day it stays full. */
export function dayFill(minutes: number, full = FULL_DAY_MINUTES): number {
  if (!(minutes > 0) || !(full > 0)) return 0
  return Math.min(1, minutes / full)
}

export interface HoursSummary {
  readonly minutes: number
  /** Distinct dates with counted time. */
  readonly daysWorked: number
  /** Forgotten clock-outs plus days of work with no shift — as `PersonHours.needsALook`. */
  readonly needsALook: number
}

/**
 * Totals over the days between `from` and `to`, inclusive. A sum of the days'
 * own whole-minute totals, so a tile above the calendar always equals the
 * cells under it added up — and both equal the pay period's report.
 */
export function summariseDays(days: readonly DayLine[], from: LocalDate, to: LocalDate): HoursSummary {
  let minutes = 0
  let daysWorked = 0
  let needsALook = 0
  for (const day of days) {
    if (day.date < from || day.date > to) continue
    minutes += day.minutes
    if (day.minutes > 0) daysWorked += 1
    needsALook += day.shifts.filter((s) => s.forgotten).length + (day.workWithoutShift ? 1 : 0)
  }
  return { minutes, daysWorked, needsALook }
}
